// Full-page Power BI report embed wrapper with agency selector and loading/error states
import * as React from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { useGetPowerBIEmbedConfigQuery } from "data/powerbi/powerbi";
import CPLoader from "shared/components/shared/CPLoader/CPLoader";
import CPFontAwesome from "shared/components/shared/CPFontAwesome/CPFontAwesome";
import CPAutoComplete from "shared/components/shared/CPAutoComplete/CPAutoComplete";
import type { CPAutoCompleteDataItem } from "shared/components/types";
import { Box } from "shared/components/base";
import "./CPPowerBIReportStyles.scss";

interface PowerBIEmbedBoundaryState {
  hasError: boolean;
}

class PowerBIEmbedBoundary extends React.Component<
  React.PropsWithChildren,
  PowerBIEmbedBoundaryState
> {
  constructor(props: React.PropsWithChildren) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): PowerBIEmbedBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(
      "[CPPowerBIReport] Render error in PowerBIEmbed:",
      error,
      info,
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            gap: 8,
            color: "var(--color-text-secondary, #6c757d)",
          }}
        >
          <span>
            The report encountered an error and could not be displayed.
          </span>
          <button
            className="k-button k-button-md k-button-solid k-button-solid-base"
            onClick={() => this.setState({ hasError: false })}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export interface CPPowerBIReportProps {
  // Report identity (matches PowerBIReport entity)
  workspaceId: string;
  reportId: string;
  /**
   * When provided, skips the Power BI GET /reports call on embed.
   * Omit to let the component resolve it automatically.
   */
  datasetId?: string | null;

  // Optional report defaults
  /** Power BI page name (section) to navigate to on load. */
  defaultPageName?: string | null;

  // Effective identity / RLS
  /** When true, an effective identity (agency code) must be passed to the embed token. */
  requiresEffectiveIdentity?: boolean;
  /**
   * Agency codes the current user is permitted to view.
   * When there are multiple, a dropdown is shown so the user can switch context.
   * When there is exactly one, it is passed automatically without showing a selector.
   */
  agencies?: string[];
  /** Pre-selected agency code. Defaults to the first entry in `agencies`. */
  defaultAgencyCode?: string | null;
  /** RLS role names to apply when requiresEffectiveIdentity is true. */
  roles?: string[];

  // Power BI filters
  /** Initial report-level filters applied on embed. */
  filters?: models.ReportLevelFilters[];

  // Viewer settings
  /** Show the Power BI filter pane. Default: false. */
  filterPaneEnabled?: boolean;
  /** Show the Power BI page navigation pane. Default: true. */
  navContentPaneEnabled?: boolean;
  /**
   * Layout display option. Default: FitToWidth.
   * @see models.DisplayOption
   */
  displayOption?: models.DisplayOption;

  // Container
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Embeds a Power BI report that fills its parent container.
 * Handles AD token acquisition, embed token generation, loading state,
 * and effective identity agency selection automatically.
 *
 * @example
 * ```tsx
 * <CPPowerBIReport
 *   workspaceId={report.workspaceId}
 *   reportId={report.reportId}
 *   requiresEffectiveIdentity={report.requiresEffectiveIdentity}
 *   agencies={user.agencies.map(a => a.code)}
 *   defaultAgencyCode={savedPin?.defaultAgencyCode}
 *   defaultPageName={report.defaultPageName}
 * />
 * ```
 */
const CPPowerBIReport: React.FC<CPPowerBIReportProps> = ({
  workspaceId,
  reportId,
  datasetId,
  defaultPageName,
  requiresEffectiveIdentity = false,
  agencies = [],
  defaultAgencyCode,
  roles = [],
  filters,
  filterPaneEnabled = false,
  navContentPaneEnabled = true,
  displayOption = models.DisplayOption.FitToWidth,
  className,
  style,
}) => {
  // Suppress unhandled promise rejections that originate from the Power BI
  // client SDK (e.g. clicking built-in filter/sort controls). In a minified
  // production bundle the stack will not contain "powerbi", so we use a
  // mounted-flag approach: any unhandled rejection that fires while this
  // component is mounted is presumed to be from the SDK and is suppressed.
  // Real application errors are caught by React's error boundary mechanism
  // (getDerivedStateFromError) rather than the unhandledrejection event.
  const isMounted = React.useRef(false);
  React.useEffect(() => {
    isMounted.current = true;
    const handleRejection = (event: PromiseRejectionEvent) => {
      if (!isMounted.current) return;
      event.preventDefault();
      console.warn(
        "[CPPowerBIReport] Suppressed unhandled rejection while report is mounted:",
        event.reason,
      );
    };
    const handleError = (event: ErrorEvent) => {
      if (!isMounted.current) return;
      // Suppress uncaught script errors while the Power BI embed is mounted.
      // The SDK throws internally when built-in visual controls are clicked.
      event.preventDefault();
      console.warn(
        "[CPPowerBIReport] Suppressed uncaught error while report is mounted:",
        event.message,
      );
    };
    window.addEventListener("unhandledrejection", handleRejection);
    window.addEventListener("error", handleError);
    return () => {
      isMounted.current = false;
      window.removeEventListener("unhandledrejection", handleRejection);
      window.removeEventListener("error", handleError);
    };
  }, []);

  // "Agent Security" is the RLS role that scopes data to a specific agency.
  // Only when this role is present (and the user manages multiple agencies) do
  // we need the user to choose which agency they are viewing as.
  const hasAgentSecurity = roles.includes("Agent Security");
  const autoAgency =
    requiresEffectiveIdentity && agencies.length === 1 ? agencies[0] : null;
  const showAgencySelector =
    requiresEffectiveIdentity && hasAgentSecurity && agencies.length > 1;

  const agencyItems = React.useMemo<CPAutoCompleteDataItem[]>(
    () => agencies.map((code) => ({ label: code, value: code })),
    [agencies],
  );

  const initialAgency = defaultAgencyCode ?? autoAgency ?? (agencies[0] || null);
  const [selectedAgencyItem, setSelectedAgencyItem] = React.useState<
    CPAutoCompleteDataItem | undefined
  >(initialAgency ? { label: initialAgency, value: initialAgency } : undefined);

  const selectedAgency = (selectedAgencyItem?.value as string | null) ?? null;

  const embedRequest =
    workspaceId && reportId
      ? {
          workspaceId,
          reportId,
          datasetId: datasetId ?? null,
          requiresEffectiveIdentity,
          agencyCode: requiresEffectiveIdentity ? selectedAgency : null,
          roles: requiresEffectiveIdentity ? roles : [],
        }
      : null;

  const {
    data: embedConfig,
    isLoading,
    isError,
    refetch,
  } = useGetPowerBIEmbedConfigQuery(embedRequest);

  const embedConfiguration: models.IReportEmbedConfiguration | null =
    embedConfig
      ? {
          type: "report",
          id: embedConfig.reportId,
          embedUrl: embedConfig.embedUrl,
          accessToken: embedConfig.embedToken,
          tokenType: models.TokenType.Embed,
          permissions: models.Permissions.All,
          pageName: defaultPageName ?? undefined,
          filters: (filters ?? []) as models.ReportLevelFilters[],
          settings: {
            filterPaneEnabled,
            navContentPaneEnabled,
            layoutType: models.LayoutType.Custom,
            customLayout: {
              displayOption,
            },
          },
        }
      : null;

  if (isError) {
    return (
      <Box
        className={`cp-powerbi-report cp-powerbi-error ${className ?? ""}`}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={style}
      >
        <CPFontAwesome
          icon="fa-solid fa-triangle-exclamation"
          size={36}
          style={{ color: "var(--color-danger, #dc3545)", marginBottom: 12 }}
        />
        <span style={{ marginBottom: 12 }}>
          Failed to load the report. Please try again.
        </span>
        <button
          className="k-button k-button-md k-button-solid k-button-solid-base"
          onClick={() => refetch()}
        >
          Retry
        </button>
      </Box>
    );
  }

  return (
    <Box
      className={`cp-powerbi-report ${className ?? ""}`}
      flexDirection="column"
      style={style}
    >
      {showAgencySelector && (
        <Box
          className="cp-powerbi-agency-bar"
          flexDirection="row"
          alignItems="center"
          spacing={8}
        >
          <CPAutoComplete
            name="agencyCode"
            label="Viewing as"
            data={agencyItems}
            value={selectedAgencyItem}
            onChange={(item) => {
              // Ignore clears — an agency is required to generate a valid embed
              // token when effectiveIdentity is enabled. Clearing the input
              // would send a null agencyCode and Power BI would reject the token.
              if (item?.value) setSelectedAgencyItem(item);
            }}
            allowManualEntry={false}
            style={{ width: 220 }}
          />
        </Box>
      )}

      <Box
        className="cp-powerbi-embed-container"
        style={{ flex: 1, minHeight: 0, position: "relative", padding: 0 }}
      >
        <CPLoader isLoading={isLoading} />
        {embedConfiguration && (
          <PowerBIEmbedBoundary>
            <PowerBIEmbed
              key={`${reportId}--${selectedAgency ?? "none"}`}
              embedConfig={embedConfiguration}
              cssClassName="cp-powerbi-iframe"
              eventHandlers={
                new Map([
                  [
                    "error",
                    (event) =>
                      console.error(
                        "[CPPowerBIReport] embed error:",
                        event?.detail,
                      ),
                  ],
                  // Register a visualClicked handler so the SDK knows the event
                  // is handled. Without this, clicking built-in visual controls
                  // (filter, sort, focus mode) causes the SDK to emit an
                  // unhandled rejection that crashes the app.
                  ["visualClicked", () => undefined],
                ])
              }
            />
          </PowerBIEmbedBoundary>
        )}
      </Box>
    </Box>
  );
};

export default CPPowerBIReport;
